/* platform/RolesPermissions — Platform Roles & Permission matrix.
   System roles: read-only. Custom roles: full CRUD backed by DB. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TenancyModal } from './_ui';
import { getPlatformRole } from './rbac.js';
import * as rolesApi from '../api/platform-roles.api.js';
import * as puApi    from '../api/platform-users.api.js';

/* All available platform permissions, grouped by module */
const PERMISSION_GROUPS = [
  { group: 'Organizations',   perms: ['Organization.Read','Organization.Write','Organization.Delete','Organization.Provision'] },
  { group: 'Workspaces',      perms: ['Workspace.Read','Workspace.Write','Workspace.Delete'] },
  { group: 'Licenses',        perms: ['License.Read','License.Write','License.Delete'] },
  { group: 'Partners',        perms: ['Partner.Read','Partner.Write','Partner.Delete'] },
  { group: 'Platform Users',  perms: ['PlatformUser.Read','PlatformUser.Update','PlatformUser.Invite','PlatformUser.Delete'] },
  { group: 'Roles',           perms: ['Role.Read','Role.Write','Role.Delete'] },
  { group: 'Audit',           perms: ['Audit.Read'] },
  { group: 'Analytics',       perms: ['Analytics.Read'] },
  { group: 'Settings',        perms: ['Settings.Read','Settings.Update'] },
  { group: 'Notifications',   perms: ['Notification.Read','Notification.Update','Notification.Delete'] },
  { group: 'System',          perms: ['SystemHealth.Read','Search.Read','Backup.Read','Backup.Write'] },
];

const ALL_PERMS = PERMISSION_GROUPS.flatMap(g => g.perms);

const SYSTEM_ROLE_PERMISSIONS = {
  SUPER_ADMIN:    ['*'],
  PLATFORM_ADMIN: ['Organization.Read','Organization.Write','Organization.Delete','Organization.Provision','Workspace.Read','Workspace.Write','Workspace.Delete','License.Read','License.Write','License.Delete','Partner.Read','Partner.Write','Partner.Delete','PlatformUser.Read','PlatformUser.Update','PlatformUser.Invite','PlatformUser.Delete','Role.Read','Role.Write','Role.Delete','Audit.Read','Analytics.Read','Settings.Read','Settings.Update','Notification.Read','Notification.Update','Notification.Delete','SystemHealth.Read','Search.Read','Backup.Read','Backup.Write'],
  SUPPORT:        ['Organization.Read','Workspace.Read','PlatformUser.Read','PlatformUser.Update','Audit.Read','SystemHealth.Read','Notification.Read','Search.Read','Analytics.Read'],
  BILLING:        ['Organization.Read','Workspace.Read','License.Read','License.Write','Analytics.Read','Notification.Read','Search.Read'],
  AUDITOR:        ['Organization.Read','Workspace.Read','License.Read','Partner.Read','Role.Read','PlatformUser.Read','Audit.Read','Analytics.Read','Notification.Read','Search.Read'],
};

const SYSTEM_ROLES = [
  { id: 'SUPER_ADMIN',    name: 'Super Admin',    isSystem: true, description: 'Unrestricted access to all platform functions. Cannot be modified.', users: 0 },
  { id: 'PLATFORM_ADMIN', name: 'Platform Admin', isSystem: true, description: 'Full platform administration excluding destructive SA-only actions.',  users: 0 },
  { id: 'SUPPORT',        name: 'Support',        isSystem: true, description: 'Read + limited update for support operations.',                         users: 0 },
  { id: 'BILLING',        name: 'Billing',        isSystem: true, description: 'License and billing management.',                                       users: 0 },
  { id: 'AUDITOR',        name: 'Auditor',        isSystem: true, description: 'Read-only access to audit, analytics, and org data.',                  users: 0 },
];

const ROLE_TONES = { SUPER_ADMIN: 'purple', PLATFORM_ADMIN: 'blue', SUPPORT: 'green', BILLING: 'amber', AUDITOR: 'gray' };
const SYSTEM_ICON_COLOR = { SUPER_ADMIN: '#6E3FBA', PLATFORM_ADMIN: '#125AB0', SUPPORT: '#0E8C5A', BILLING: '#C26A1F', AUDITOR: '#64748B' };

const EMPTY_FORM = { name: '', description: '', permissions: [] };

/* ── Permission matrix — used in Create/Edit modals (editable) and detail panel (read-only) ── */
function PermMatrix({ permissions, onChange, disabled }) {
  const has = (p) => permissions.includes('*') || permissions.includes(p);
  const toggle = (p) => {
    if (disabled) return;
    onChange(permissions.includes(p) ? permissions.filter(x => x !== p) : [...permissions, p]);
  };
  const toggleGroup = (perms) => {
    if (disabled) return;
    const allOn = perms.every(p => permissions.includes(p));
    if (allOn) onChange(permissions.filter(p => !perms.includes(p)));
    else        onChange([...new Set([...permissions, ...perms])]);
  };

  const isWildcard = permissions.includes('*');
  const selectedCount = isWildcard ? ALL_PERMS.length : ALL_PERMS.filter(p => permissions.includes(p)).length;
  let globalState = 'partial';
  if (isWildcard || selectedCount === ALL_PERMS.length) globalState = 'all';
  else if (selectedCount === 0) globalState = 'none';
  const handleGlobal = () => {
    if (disabled || isWildcard) return;
    onChange(globalState === 'all' ? [] : [...ALL_PERMS]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!disabled && (
        <div
          aria-label="Global permission selector"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderRadius: 6, marginBottom: 2,
            border: '1.5px solid',
            borderColor: globalState !== 'none' ? 'var(--nx-accent)' : 'var(--nx-border)',
            background: globalState !== 'none' ? 'var(--nx-accent)0d' : 'transparent',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              aria-hidden="true"
              style={{
                width: 15, height: 15, borderRadius: 3, border: '1.5px solid', flexShrink: 0,
                borderColor: globalState !== 'none' ? 'var(--nx-accent)' : 'var(--nx-border)',
                background: globalState === 'all' ? 'var(--nx-accent)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {globalState === 'all' && (
                <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                  <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {globalState === 'partial' && (
                <span style={{ width: 7, height: 2, background: 'var(--nx-accent)', borderRadius: 1, display: 'block' }} />
              )}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text)' }}>All Permissions</span>
            <span data-testid="rp-global-count" style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
              {selectedCount}/{ALL_PERMS.length}
            </span>
          </div>
          <button
            data-testid="rp-global-toggle"
            aria-label={globalState === 'all' ? 'Clear all permissions' : 'Select all permissions'}
            onClick={handleGlobal}
            disabled={isWildcard}
            style={{
              fontSize: 11, color: 'var(--nx-accent)', background: 'none', border: 'none',
              cursor: isWildcard ? 'default' : 'pointer', padding: '2px 8px',
              borderRadius: 4, fontWeight: 700, opacity: isWildcard ? 0.5 : 1,
            }}
          >
            {globalState === 'all' ? 'Clear all' : 'Select all permissions'}
          </button>
        </div>
      )}
      {PERMISSION_GROUPS.map(({ group, perms }) => (
        <div key={group} style={{ paddingBottom: 10, borderBottom: '1px solid var(--nx-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{
              fontSize: 10.5, fontWeight: 800, color: 'var(--nx-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1,
            }}>
              {group}
            </span>
            {!disabled && (
              <button
                style={{
                  fontSize: 11, color: 'var(--nx-accent)', background: 'none', border: 'none',
                  cursor: 'pointer', padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                  fontWeight: 600,
                }}
                onClick={() => toggleGroup(perms)}
              >
                {perms.every(p => has(p)) ? 'Remove all' : 'Select all'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {perms.map(p => (
              <button key={p}
                onClick={() => toggle(p)}
                disabled={disabled || permissions.includes('*')}
                style={{
                  padding: '4px 0', fontSize: 12, borderRadius: 4, border: '1.5px solid',
                  minWidth: 72, textAlign: 'center',
                  cursor: disabled || permissions.includes('*') ? 'default' : 'pointer',
                  background: has(p) ? 'var(--nx-accent)' : 'transparent',
                  color:      has(p) ? '#fff' : 'var(--nx-text-muted)',
                  borderColor: has(p) ? 'var(--nx-accent)' : 'var(--nx-border)',
                  transition: 'all 0.12s',
                }}>
                {p.split('.')[1] || p}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Single compact role row in the left panel ── */
function RoleRow({ role, isSystem, isActive, isSA, onSelect, onEdit, onDuplicate, onDelete }) {
  const iconColor = isSystem ? (SYSTEM_ICON_COLOR[role.id] || '#64748B') : 'var(--nx-accent)';
  const iconBg = isSystem ? `color-mix(in srgb, ${SYSTEM_ICON_COLOR[role.id] || '#64748B'} 12%, transparent)` : 'color-mix(in srgb, var(--nx-accent) 10%, transparent)';
  const permCount = isSystem
    ? (SYSTEM_ROLE_PERMISSIONS[role.id]?.includes('*') ? ALL_PERMS.length : (SYSTEM_ROLE_PERMISSIONS[role.id] || []).length)
    : (role.permissions || []).length;

  return (
    <div
      data-role-id={role.id}
      data-testid={isSystem ? `rp-sysrole-${role.id}` : `rp-role-${role.id}`}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      aria-pressed={isActive}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        cursor: 'pointer', userSelect: 'none', transition: 'background 0.12s',
        borderLeft: '2.5px solid',
        borderLeftColor: isActive ? 'var(--nx-accent)' : 'transparent',
        background: isActive ? 'color-mix(in srgb, var(--nx-accent) 7%, transparent)' : 'transparent',
      }}
    >
      {/* Icon badge */}
      <div style={{
        width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, background: isActive ? iconBg : 'var(--nx-stroke)', color: isActive ? iconColor : 'var(--nx-text-muted)',
        transition: 'all 0.12s',
      }}>
        <NexusIcon name={isSystem ? 'ShieldCheck' : 'Shield'} size={15} />
      </div>

      {/* Name + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: isActive ? 'var(--nx-text)' : 'var(--nx-text)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {role.name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {permCount} perm{permCount !== 1 ? 's' : ''}
          {role.description ? ` · ${role.description}` : ''}
        </div>
      </div>

      {/* Action buttons (custom only, SA only) */}
      {!isSystem && isSA && (
        <div
          className="rp-row-actions"
          style={{ display: 'flex', gap: 1, flexShrink: 0, opacity: isActive ? 1 : 0 }}
          onClick={e => e.stopPropagation()}
        >
          <button title="Edit" style={{ padding: '3px 4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', borderRadius: 4 }} onClick={onEdit}>
            <NexusIcon name="Pencil" size={12} />
          </button>
          <button title="Duplicate" style={{ padding: '3px 4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', borderRadius: 4 }} onClick={onDuplicate}>
            <NexusIcon name="Copy" size={12} />
          </button>
          <button title="Delete" style={{ padding: '3px 4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-danger)', borderRadius: 4 }} onClick={onDelete}>
            <NexusIcon name="Trash" size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main component ── */
export default function RolesPermissions() {
  const toast  = useToast();
  const myRole = getPlatformRole();
  const isSA   = myRole === 'SUPER_ADMIN';

  const [customRoles, setCustomRoles]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);
  const [selectedIsSystem, setSelectedIsSystem] = useState(false);
  const [selectedUsers, setSelectedUsers]         = useState([]);
  const [selectedUsersLoading, setSelectedUsersLoading] = useState(false);
  const [selectedUsersError,   setSelectedUsersError]   = useState(false);

  /* BUG-008: role type tabs + search */
  const [activeTab, setActiveTab]     = useState('custom');
  const [searchQuery, setSearchQuery] = useState('');

  const [createModal, setCreateModal] = useState(false);
  const [editModal,   setEditModal]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm]   = useState(EMPTY_FORM);
  const [busy, setBusy]   = useState(false);

  const pendingSelectId    = useRef(null);
  const roleListRef        = useRef(null);
  const detailPanelRef     = useRef(null);
  /* BUG-010: race-condition guard (incremented on every role selection) */
  const selectionGen       = useRef(0);
  /* BUG-010: caches GET /platform/users for the page session — avoids repeated fetches
     when switching between system roles without leaving the page */
  const platformUsersCache = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    rolesApi.list()
      .then(res => setCustomRoles(Array.isArray(res) ? res.filter(r => !r.isSystem) : []))
      .catch(() => setCustomRoles([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  /* BUG-010: unified assigned-users loader for both role types.
     Uses a generation counter to discard stale responses from rapid role switching.
     System roles: GET /platform/users → filter by platformRole (cached per session).
     Custom roles: GET /platform/roles/:uuid → role.users (Prisma include). */
  const loadRoleUsers = useCallback((role, isSystem) => {
    const gen = ++selectionGen.current;
    setSelectedUsers([]);
    setSelectedUsersLoading(true);
    setSelectedUsersError(false);

    const applyIfCurrent = (users, error) => {
      if (selectionGen.current !== gen) return; // stale — a newer selection won
      if (error) { setSelectedUsersError(true); setSelectedUsersLoading(false); return; }
      setSelectedUsers(users);
      setSelectedUsersLoading(false);
    };

    if (isSystem && role?.id) {
      const roleId = role.id;
      if (platformUsersCache.current) {
        applyIfCurrent(platformUsersCache.current.filter(u => u.platformRole === roleId));
      } else {
        puApi.list()
          .then(users => {
            if (Array.isArray(users)) platformUsersCache.current = users;
            applyIfCurrent((platformUsersCache.current || []).filter(u => u.platformRole === roleId));
          })
          .catch(() => applyIfCurrent([], true));
      }
    } else if (!isSystem && role?.id) {
      rolesApi.getOne(role.id)
        .then(full => applyIfCurrent(full.users || []))
        .catch(() => applyIfCurrent([], true));
    } else {
      applyIfCurrent([]);
    }
  }, []);

  /* Auto-select: newest custom role on initial load; re-select after create/edit/delete.
     Also syncs activeTab to match the selected role type. */
  useEffect(() => {
    if (loading) return;
    const doSelect = (role, isSystem) => {
      setActiveTab(isSystem ? 'system' : 'custom');
      setSelected(role);
      setSelectedIsSystem(isSystem);
      loadRoleUsers(role, isSystem);
    };
    const pending = pendingSelectId.current;
    if (pending) {
      pendingSelectId.current = null;
      const target = customRoles.find(r => r.id === pending);
      if (target) { doSelect(target, false); return; }
    }
    if (selectedIsSystem) return;
    if (!selected) {
      if (customRoles.length > 0) doSelect(customRoles[0], false);
      else doSelect(SYSTEM_ROLES[0], true);
      return;
    }
    if (!customRoles.some(r => r.id === selected.id)) {
      if (customRoles.length > 0) doSelect(customRoles[0], false);
      else doSelect(SYSTEM_ROLES[0], true);
    }
  }, [loading, customRoles, selected, selectedIsSystem]);

  /* Reset detail panel scroll to top on role change */
  useEffect(() => {
    if (detailPanelRef.current) detailPanelRef.current.scrollTop = 0;
  }, [selected?.id]);

  /* Scroll selected role row into view in the left panel */
  useEffect(() => {
    if (!selected?.id || !roleListRef.current) return;
    const el = roleListRef.current.querySelector(`[data-role-id="${selected.id}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected?.id]);

  /* Tab switch: clears search, auto-selects first role in the new tab */
  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    if (tab === 'system') {
      setSelected(SYSTEM_ROLES[0]);
      setSelectedIsSystem(true);
      loadRoleUsers(SYSTEM_ROLES[0], true);
    } else {
      if (customRoles.length > 0) {
        const first = customRoles[0];
        setSelected(first);
        setSelectedIsSystem(false);
        loadRoleUsers(first, false);
      }
    }
  }, [customRoles, loadRoleUsers]);

  const selectRole = (role, isSystem) => {
    setSelected(role);
    setSelectedIsSystem(isSystem);
    loadRoleUsers(role, isSystem);
  };

  const handleCreate = async () => {
    if (!form.name.trim() || form.permissions.length === 0) return;
    setBusy(true);
    try {
      const newRole = await rolesApi.create(form.name.trim(), form.description.trim(), form.permissions);
      toast({ tone: 'success', icon: 'Plus', title: 'Role created', desc: form.name });
      setCreateModal(false);
      setForm(EMPTY_FORM);
      pendingSelectId.current = newRole?.id ?? null;
      load();
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
    finally { setBusy(false); }
  };

  const handleEdit = async () => {
    if (!form.name.trim() || form.permissions.length === 0) return;
    setBusy(true);
    try {
      await rolesApi.update(editModal.id, { name: form.name.trim(), description: form.description.trim(), permissions: form.permissions });
      toast({ tone: 'success', icon: 'Check', title: 'Role updated' });
      setEditModal(null);
      setForm(EMPTY_FORM);
      pendingSelectId.current = editModal.id;
      load();
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
    finally { setBusy(false); }
  };

  const handleDuplicate = async (role) => {
    try {
      await rolesApi.duplicate(role.id);
      toast({ tone: 'success', icon: 'Copy', title: 'Role duplicated', desc: role.name + ' (copy)' });
      load();
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
  };

  const handleDelete = async () => {
    try {
      await rolesApi.remove(deleteConfirm.id);
      toast({ tone: 'warning', icon: 'Trash', title: 'Role deleted', desc: deleteConfirm.name });
      setDeleteConfirm(null);
      load();
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
  };

  const openEdit = (role) => {
    setForm({ name: role.name, description: role.description || '', permissions: role.permissions || [] });
    setEditModal(role);
  };

  const openCreate = () => { setForm(EMPTY_FORM); setCreateModal(true); };

  /* Filtered role lists — search within current tab */
  const q = searchQuery.toLowerCase().trim();
  const filteredCustomRoles = q
    ? customRoles.filter(r => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q))
    : customRoles;
  const filteredSystemRoles = q
    ? SYSTEM_ROLES.filter(r => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q))
    : SYSTEM_ROLES;

  /* Permissions for the detail panel */
  const viewPerms = selected
    ? (selectedIsSystem ? (SYSTEM_ROLE_PERMISSIONS[selected.id] || []) : (selected.permissions || []))
    : [];
  const permDisplayCount = viewPerms.includes('*') ? ALL_PERMS.length : viewPerms.length;
  const iconColor = selected
    ? (selectedIsSystem ? (SYSTEM_ICON_COLOR[selected.id] || '#64748B') : 'var(--nx-accent)')
    : 'var(--nx-text-muted)';
  const iconBg = selected
    ? (selectedIsSystem ? `color-mix(in srgb, ${SYSTEM_ICON_COLOR[selected.id] || '#64748B'} 12%, transparent)` : 'color-mix(in srgb, var(--nx-accent) 10%, transparent)')
    : 'var(--nx-stroke)';

  /* Tab style helper */
  const tabStyle = (tab) => ({
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '10px 4px', border: 'none', background: 'none', cursor: 'pointer',
    borderBottom: activeTab === tab ? '2px solid var(--nx-accent)' : '2px solid transparent',
    color: activeTab === tab ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
    fontWeight: activeTab === tab ? 700 : 500, fontSize: 13,
    transition: 'all 0.12s', whiteSpace: 'nowrap',
  });

  return (
    /* Viewport-bound: fills nx-cc-content height with no browser-level scroll */
    <div
      data-testid="rp-root"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >

      {/* ── Page header (shrinks to fit content) ── */}
      <div style={{ padding: '18px 26px 10px', flexShrink: 0 }}>
        <PageHead
          title="Roles & Permissions"
          sub="System roles are read-only. Custom roles can be created and assigned to platform users."
          actions={isSA ? <Btn variant="primary" icon="Plus" onClick={openCreate}>New Role</Btn> : null}
        />
        <div className="nx-cc-stat-grid nx-kpi-4" style={{ marginTop: 10 }}>
          <StatCard label="System Roles"  value={SYSTEM_ROLES.length}                     icon="ShieldCheck" color="#6E3FBA" />
          <StatCard label="Custom Roles"  value={loading ? '…' : customRoles.length}      icon="Shield"      color="#125AB0" />
          <StatCard label="Total Roles"   value={loading ? '…' : SYSTEM_ROLES.length + customRoles.length} icon="Users" color="#0E8C5A" />
          <StatCard label="Permissions"   value={ALL_PERMS.length}                         icon="Key"         color="#C26A1F" />
        </div>
      </div>

      {/* ── Master-detail workspace (fills remaining height) ── */}
      <div
        data-testid="rp-workspace"
        style={{
          flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden',
          margin: '0 26px 20px',
          border: '1px solid var(--nx-border)', borderRadius: 10,
          background: 'var(--nx-surface)',
        }}
      >

        {/* ── Left panel: role navigation ── */}
        <div
          data-testid="rp-left-panel"
          style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--nx-border)' }}
        >

          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', flexShrink: 0 }}>
            <button
              data-testid="rp-tab-system"
              onClick={() => switchTab('system')}
              style={tabStyle('system')}
              aria-selected={activeTab === 'system'}
              role="tab"
            >
              <NexusIcon name="ShieldCheck" size={13} />
              System
              <Pill tone="gray">{SYSTEM_ROLES.length}</Pill>
            </button>
            <button
              data-testid="rp-tab-custom"
              onClick={() => switchTab('custom')}
              style={tabStyle('custom')}
              aria-selected={activeTab === 'custom'}
              role="tab"
            >
              <NexusIcon name="Shield" size={13} />
              Custom
              <Pill tone="blue">{loading ? '…' : customRoles.length}</Pill>
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--nx-border)', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <NexusIcon name="Search" size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-faint)', pointerEvents: 'none' }} />
              <input
                data-testid="rp-search"
                type="search"
                className="nx-input"
                placeholder="Search roles…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ paddingLeft: 28, fontSize: 12, height: 30, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Role list — unified scroll */}
          <div
            ref={roleListRef}
            data-testid="rp-role-list"
            style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
          >
            {activeTab === 'system' ? (
              filteredSystemRoles.length === 0 ? (
                <div className="nx-empty" style={{ padding: '24px 16px', flexDirection: 'column' }}>
                  <NexusIcon name="SearchX" size={18} />
                  <span style={{ fontSize: 12 }}>No system roles match.</span>
                </div>
              ) : filteredSystemRoles.map(r => (
                <RoleRow
                  key={r.id}
                  role={r}
                  isSystem={true}
                  isActive={selected?.id === r.id && selectedIsSystem}
                  isSA={isSA}
                  onSelect={() => selectRole(r, true)}
                />
              ))
            ) : loading ? (
              <div className="nx-empty" style={{ padding: '24px 16px' }}>
                <NexusIcon name="Loader" size={16} />
                <span>Loading…</span>
              </div>
            ) : filteredCustomRoles.length === 0 ? (
              <div className="nx-empty" style={{ padding: '24px 16px', flexDirection: 'column' }}>
                <NexusIcon name={searchQuery ? 'SearchX' : 'Shield'} size={18} />
                <span style={{ fontSize: 12 }}>
                  {searchQuery ? `No roles match "${searchQuery}"` : 'No custom roles yet.'}
                </span>
                {!searchQuery && isSA && (
                  <Btn variant="secondary" size="sm" icon="Plus" onClick={openCreate}>Create Role</Btn>
                )}
              </div>
            ) : filteredCustomRoles.map(r => (
              <RoleRow
                key={r.id}
                role={r}
                isSystem={false}
                isActive={selected?.id === r.id && !selectedIsSystem}
                isSA={isSA}
                onSelect={() => selectRole(r, false)}
                onEdit={() => openEdit(r)}
                onDuplicate={() => handleDuplicate(r)}
                onDelete={() => setDeleteConfirm(r)}
              />
            ))}
          </div>

          {/* New Role action footer (custom tab, SA only) */}
          {isSA && activeTab === 'custom' && (
            <div style={{ padding: '8px 10px', borderTop: '1px solid var(--nx-border)', flexShrink: 0 }}>
              <button
                onClick={openCreate}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '7px 12px', borderRadius: 6, border: '1.5px dashed var(--nx-border)',
                  background: 'transparent', color: 'var(--nx-accent)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                <NexusIcon name="Plus" size={13} />
                New Role
              </button>
            </div>
          )}
        </div>

        {/* ── Right panel: role detail ── */}
        <div
          ref={detailPanelRef}
          data-testid="rp-detail-panel"
          style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {!selected ? (
            <div className="nx-empty" style={{ height: '100%', minHeight: 200 }}>
              <NexusIcon name={loading ? 'Loader' : 'ShieldCheck'} size={24} />
              <span>{loading ? 'Loading…' : 'Select a role to view its permissions'}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

              {/* Detail header */}
              <div
                data-testid="rp-detail-header"
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '18px 22px 14px',
                  borderBottom: '1px solid var(--nx-border)',
                  flexShrink: 0,
                }}
              >
                {/* Role icon badge */}
                <div
                  data-testid="rp-detail-icon"
                  style={{
                    width: 46, height: 46, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, background: iconBg, color: iconColor,
                  }}
                >
                  <NexusIcon name={selectedIsSystem ? 'ShieldCheck' : 'Shield'} size={22} />
                </div>

                {/* Name + badge only — description moves to content area to keep row height = icon height */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    <h3
                      data-testid="rp-detail-name"
                      style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)', margin: 0, lineHeight: '1.2', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {selected.name}
                    </h3>
                    {selectedIsSystem
                      ? <Pill tone={ROLE_TONES[selected.id] || 'gray'}>system</Pill>
                      : <Pill tone="blue">custom</Pill>
                    }
                  </div>
                </div>

                {/* Permission count — flex column so number + label are mutually centered */}
                <div
                  data-testid="rp-perm-count"
                  style={{
                    flexShrink: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    minWidth: 52,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nx-text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                    {permDisplayCount}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>
                    perms
                  </div>
                </div>

                {/* Action buttons (custom roles only) */}
                {!selectedIsSystem && isSA && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                    <Btn variant="ghost" size="sm" icon="Pencil" onClick={() => openEdit(selected)}>Edit</Btn>
                    <Btn variant="ghost" size="sm" icon="Copy"   onClick={() => handleDuplicate(selected)}>Duplicate</Btn>
                    <Btn variant="ghost" size="sm" icon="Trash"  onClick={() => setDeleteConfirm(selected)}>Delete</Btn>
                  </div>
                )}
              </div>

              {/* Permission matrix area */}
              <div style={{ flex: 1, padding: '16px 22px', overflowY: 'visible' }}>
                {/* Description lives here (not in header) so header row height stays stable */}
                {selected.description && (
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: '1.5' }}>
                    {selected.description}
                  </p>
                )}
                {viewPerms.includes('*') ? (
                  <div style={{
                    padding: '12px 16px', background: 'color-mix(in srgb, #6E3FBA 8%, transparent)',
                    borderRadius: 8, color: '#6E3FBA', fontSize: 13, fontWeight: 600, marginBottom: 16,
                    border: '1px solid color-mix(in srgb, #6E3FBA 25%, transparent)',
                  }}>
                    Super Admin — all permissions (*). Unrestricted platform access.
                  </div>
                ) : null}
                <PermMatrix permissions={viewPerms} onChange={() => {}} disabled />

                {/* Assigned Users — shown for both system and custom roles */}
                <div
                  data-testid="rp-assigned-users"
                  style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--nx-border)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Assigned Users {!selectedUsersLoading && !selectedUsersError ? `(${selectedUsers.length})` : ''}
                    </div>
                    {selectedUsersLoading && (
                      <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>Loading…</span>
                    )}
                  </div>

                  {/* System role: informational context note */}
                  {selectedIsSystem && !selectedUsersLoading && !selectedUsersError && (
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <NexusIcon name="Info" size={11} style={{ flexShrink: 0 }} />
                      Assigned through Platform Users
                    </div>
                  )}

                  {/* Error state */}
                  {selectedUsersError && (
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '8px 0' }}>
                      Unable to load assigned users.
                    </div>
                  )}

                  {/* Loading skeleton */}
                  {selectedUsersLoading && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[1, 2].map(i => (
                        <div key={i} style={{ height: 20, borderRadius: 4, background: 'var(--nx-stroke)', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
                      ))}
                    </div>
                  )}

                  {/* Empty state */}
                  {!selectedUsersLoading && !selectedUsersError && selectedUsers.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                      {selectedIsSystem
                        ? <>No users currently have this system role. <br />Assignments are managed through Platform Users.</>
                        : 'No users assigned to this role.'
                      }
                    </div>
                  )}

                  {/* User list */}
                  {!selectedUsersLoading && !selectedUsersError && selectedUsers.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                      <NexusIcon name="User" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                      <span style={{ fontSize: 13 }}>{u.name || u.email}</span>
                      <span className="nx-muted nx-mini" style={{ marginLeft: 'auto' }}>{u.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Create modal ── */}
      {createModal && (
        <TenancyModal
          title="Create Custom Role"
          subtitle="Define a name and select the permissions this role grants."
          icon="Shield"
          onClose={() => setCreateModal(false)}
          width={680}
          dialog
          footer={
            <>
              <Btn variant="ghost" onClick={() => setCreateModal(false)}>Cancel</Btn>
              <Btn variant="primary" icon="Plus" disabled={busy || !form.name.trim() || form.permissions.length === 0} onClick={handleCreate}
                data-testid="rp-create-submit">
                {busy ? 'Creating…' : 'Create Role'}
              </Btn>
            </>
          }
        >
          <div className="nx-form" style={{ gap: 12 }}>
            <div className="nx-form-row">
              <label htmlFor="rp-create-name">Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input id="rp-create-name" className="nx-input" placeholder="e.g. Compliance Manager" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="nx-form-row">
              <label htmlFor="rp-create-desc">Description</label>
              <input id="rp-create-desc" className="nx-input" placeholder="Optional description" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)', marginBottom: 8 }}>Permissions</div>
              {form.permissions.length === 0 && (
                <div data-testid="rp-create-perm-error" style={{ fontSize: 12, color: 'var(--nx-danger)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <NexusIcon name="AlertCircle" size={12} style={{ flexShrink: 0 }} />
                  Select at least one permission.
                </div>
              )}
              <PermMatrix permissions={form.permissions} onChange={p => setForm(f => ({ ...f, permissions: p }))} />
            </div>
          </div>
        </TenancyModal>
      )}

      {/* ── Edit modal ── */}
      {editModal && (
        <TenancyModal
          title={'Edit Role — ' + editModal.name}
          subtitle="Rename or change the permissions granted by this role."
          icon="Pencil"
          onClose={() => setEditModal(null)}
          width={680}
          dialog
          footer={
            <>
              <Btn variant="ghost" onClick={() => setEditModal(null)}>Cancel</Btn>
              <Btn variant="primary" icon="Check" disabled={busy || !form.name.trim() || form.permissions.length === 0} onClick={handleEdit}
                data-testid="rp-edit-submit">
                {busy ? 'Saving…' : 'Save Changes'}
              </Btn>
            </>
          }
        >
          <div className="nx-form" style={{ gap: 12 }}>
            <div className="nx-form-row">
              <label htmlFor="rp-edit-name">Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input id="rp-edit-name" className="nx-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="nx-form-row">
              <label htmlFor="rp-edit-desc">Description</label>
              <input id="rp-edit-desc" className="nx-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)', marginBottom: 8 }}>Permissions</div>
              {form.permissions.length === 0 && (
                <div data-testid="rp-edit-perm-error" style={{ fontSize: 12, color: 'var(--nx-danger)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <NexusIcon name="AlertCircle" size={12} style={{ flexShrink: 0 }} />
                  Select at least one permission.
                </div>
              )}
              <PermMatrix permissions={form.permissions} onChange={p => setForm(f => ({ ...f, permissions: p }))} />
            </div>
          </div>
        </TenancyModal>
      )}

      {/* ── Delete confirm ── */}
      {deleteConfirm && (
        <TenancyModal title={'Delete Role — ' + deleteConfirm.name + '?'} icon="Trash" danger onClose={() => setDeleteConfirm(null)}>
          <p className="nx-confirm-desc">
            Deleting this role removes it permanently. Users assigned this role will lose their custom permissions immediately.
            Their platform access (if any) must be reassigned to a system role.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={handleDelete}>Delete Role</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
